import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Game from "@/assets/icons/gamepad.svg?react";
import { BgtSimpleSwitch } from "@/components/BgtForm";
import { BgtImageCard } from "@/components/BgtImageCard/BgtImageCard";
import { BgtCardList } from "@/components/BgtLayout/BgtCardList";
import { BgtEmptyPage } from "@/components/BgtLayout/BgtEmptyPage";
import { BgtPage } from "@/components/BgtLayout/BgtPage";
import { BgtPageContent } from "@/components/BgtLayout/BgtPageContent";
import BgtPageHeader from "@/components/BgtLayout/BgtPageHeader";
import { BgtText } from "@/components/BgtText/BgtText";
import { usePermissions } from "@/hooks/usePermissions";
import { useStoredFlag } from "@/hooks/useStoredFlag";
import { getGames } from "@/services/queries/games";
import { getSettings } from "@/services/queries/settings";
import { GameCardOverlay } from "./-components/GameCardOverlay";
import type { GamesFilterSearch } from "./-components/GamesFilters";
import { GamesPagination } from "./-components/GamesPagination";
import { GamesToolbar } from "./-components/GamesToolbar";
import { DEFAULT_PAGE_SIZE, mergeSearch, validateGamesSearch } from "./-components/gamesSearch";
import { useGamesView } from "./-hooks/useGamesView";

/** Remembered per browser: a view preference nobody else needs to know about. */
const OVERLAY_OFF_KEY = "bgt-games-overlay-disabled";

export const Route = createFileRoute("/games/")({
	component: RouteComponent,
	loader: ({ context: { queryClient } }) => {
		queryClient.prefetchQuery(getGames());
		queryClient.prefetchQuery(getSettings());
	},
	validateSearch: validateGamesSearch,
});

function RouteComponent() {
	const search = Route.useSearch();
	const { t } = useTranslation(["games", "dashboard", "common"]);
	const navigate = useNavigate();
	const { canWrite } = usePermissions();
	const [overlayOff, setOverlayOff] = useStoredFlag(OVERLAY_OFF_KEY);

	const updateSearch = useCallback(
		(partial: Partial<GamesFilterSearch>, replace = false) => {
			navigate({ to: "/games", search: (prev) => mergeSearch(prev, partial), replace });
		},
		[navigate],
	);

	const view = useGamesView(search, updateSearch);

	if (view.isLoading) return null;

	if (view.games.length === 0) {
		return (
			<BgtEmptyPage
				header={t("title")}
				icon={Game}
				title={t("dashboard:empty.title")}
				description={t("dashboard:empty.description")}
				action={canWrite ? { onClick: () => navigate({ to: "/games/add" }), label: t("new") } : undefined}
			/>
		);
	}

	return (
		<BgtPage>
			<BgtPageHeader
				header={t("title")}
				icon={Game}
				actions={[
					{
						// The same query, shown as a table - so the search comes along.
						onClick: () => navigate({ to: "/games/table", search: (prev) => prev }),
						variant: "cancel",
						content: "common:games-table",
					},
					...(canWrite
						? [
								{
									onClick: () => navigate({ to: "/games/add" }),
									variant: "primary" as const,
									content: "games:new",
								},
							]
						: []),
				]}
			>
				<BgtSimpleSwitch label={t("games:overlay.disable")} value={overlayOff} onChange={setOverlayOff} />
			</BgtPageHeader>
			<BgtPageContent>
				<GamesToolbar
					types={view.types}
					categories={view.categories}
					mechanics={view.mechanics}
					search={search}
					sortRules={view.sortRules}
					status={view.status}
					canSortByPlays={view.canSortByPlays}
					nameValue={view.nameValue}
					onNameChange={view.setNameValue}
					onSortChange={view.setSortRules}
					onSearchChange={updateSearch}
				/>
				<BgtText size="3" color="primary" className="pb-6" weight="medium">
					{t("count", { count: view.filtered.length })}
				</BgtText>
				<BgtCardList>
					{view.paged.map((x) => (
						<BgtImageCard
							key={x.id}
							title={x.title}
							image={x.image}
							state={x.state}
							isLoaned={x.isLoaned}
							link={`/games/${x.id}`}
							overlay={overlayOff ? null : <GameCardOverlay game={x} />}
						/>
					))}
				</BgtCardList>
				<GamesPagination
					page={view.page}
					pageCount={view.pageCount}
					size={view.size}
					onPageChange={(page) => updateSearch({ page: page > 1 ? page : undefined })}
					// Back to the first page: page nine of the old size is not
					// page nine of the new one.
					onSizeChange={(size) =>
						updateSearch({ size: size === DEFAULT_PAGE_SIZE ? undefined : size, page: undefined })
					}
				/>
			</BgtPageContent>
		</BgtPage>
	);
}
